/**
 * The SOW generator's request-type writer and preview checks (RESTORE_PLAN
 * R3.5, carried from the never-wired full-page SOWGeneratorPage): a scope
 * paragraph and five standard deliverables for each ticked request type,
 * about 40 hours each, and the payment-due choices the old Terms tab had.
 */

export interface RequestTypeLite {
  id: string;
  name: string;
  description?: string;
  fieldCount?: number;
}

export const HOURS_PER_REQUEST_TYPE = 40;

/** When invoices are due (the old "Payment terms"); the live free-text field is the payment schedule. */
export const PAYMENT_DUE_OPTIONS: readonly string[] = ['Due on receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60'];

/** A request type as the generator lists it: name, description and field count when the config item carries them. */
export function requestTypeFromConfigItem(item: any): RequestTypeLite {
  const fields = Array.isArray(item?.fields) ? item.fields : Array.isArray(item?.data?.fields) ? item.data.fields : null;
  return {
    id: String(item?.id),
    name: String(item?.name ?? item?.id),
    ...(typeof item?.description === 'string' && item.description.trim() ? { description: item.description.trim() } : {}),
    ...(fields ? { fieldCount: fields.length } : {}),
  };
}

/** The scope text, deliverables and hours the old page wrote from its ticked request types. */
export function scopeFromRequestTypes(types: RequestTypeLite[]): { scope: string; deliverables: string[]; hours: number } {
  const scope = types
    .map((rt) => {
      const lines = [rt.name, rt.description || 'Configuration and implementation'];
      if (typeof rt.fieldCount === 'number') lines.push(`- ${rt.fieldCount} custom fields configured`);
      lines.push('- Queue routing and workflow', '- Priority matrix');
      return lines.join('\n');
    })
    .join('\n\n');
  const deliverables = types.flatMap((rt) => [
    `${rt.name} - Complete field configuration${typeof rt.fieldCount === 'number' ? ` (${rt.fieldCount} fields)` : ''}`,
    `${rt.name} - Queue routing and workflow setup`,
    `${rt.name} - Priority matrix configuration`,
    `${rt.name} - Testing and validation`,
    `${rt.name} - Documentation and training materials`,
  ]);
  return { scope, deliverables, hours: types.length * HOURS_PER_REQUEST_TYPE };
}

/** Folds the written scope into the SOW: scope appended, deliverables de-duplicated, hours kept when already set. */
export function applyRequestTypeScope<T extends { scope: string; deliverables: string[]; estimatedHours: number }>(sow: T, types: RequestTypeLite[]): T {
  if (types.length === 0) return sow;
  const written = scopeFromRequestTypes(types);
  return {
    ...sow,
    scope: sow.scope.trim() ? `${sow.scope.trim()}\n\n${written.scope}` : written.scope,
    deliverables: [...new Set([...sow.deliverables, ...written.deliverables])],
    estimatedHours: sow.estimatedHours || written.hours,
  };
}

/** What the preview needs first (the old page's step check): a title, a scope and at least one deliverable. */
export function previewProblems(sow: { title: string; scope: string; deliverables: string[] }): string[] {
  const out: string[] = [];
  if (!sow.title.trim()) out.push('a project title');
  if (!sow.scope.trim()) out.push('a scope of work');
  if (sow.deliverables.length === 0) out.push('at least one deliverable');
  return out;
}
